import { forwardRef, useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';
import { s4 } from '@ardoq/design-tokens';
import { ComponentMatrix2Commands, RowElementModel } from '../types';
import { ComponentMatrixRowHeader } from './componentMatrixRowHeader';
import { ArdoqId } from '@ardoq/api-types';

// 4px right padding combines with `BODY_CELL_CONTAINER_HORIZONTAL_MARGIN` on
// the first body cell for an 8px visible gap to the body (ARD-31376, Figma
// node 9339-19925). Mirrors the existing level-headers convention (see
// `componentMatrixLevelHeadersArea`), so the row-header card's right edge
// aligns with the level-headers' card right edge above it.
const ComponentMatrixRowHeaderContainer = styled.div`
  overflow: hidden;
  display: grid;
  grid-row: 2;
  grid-column: 1;
  grid-template-columns: 100%;
  padding-right: ${s4};
`;

type ComponentMatrixRowHeadersAreaProperties = {
  rowModels: RowElementModel[];
  rowHeights: number[];
  width: number;
  horizontalScrollbarHeight: number;
  hoveredComponentId: ArdoqId | null;
  focusedComponentId: ArdoqId | null;
  contextComponentId: ArdoqId | null;
  commands: ComponentMatrix2Commands;
};
export type ComponentMatrixRowHeadersAreaRef = {
  setScrollTop: (scrollTop: number) => void;
};
const ComponentMatrixRowHeadersArea = forwardRef<
  ComponentMatrixRowHeadersAreaRef,
  ComponentMatrixRowHeadersAreaProperties
>((props, ref) => {
  const container = useRef<HTMLDivElement>(null);

  const setScrollTop = (value: number) => {
    if (!container.current) {
      return;
    }
    container.current.scrollTop = value;
  };
  useImperativeHandle(ref, () => ({ setScrollTop }));

  const {
    rowModels,
    rowHeights,
    width,
    horizontalScrollbarHeight,
    hoveredComponentId,
    focusedComponentId,
    contextComponentId,
    commands,
  } = props;

  const rowBottomGaps = rowModels.map(rowModel => rowModel.rowBottomGap ?? 0);

  return (
    <ComponentMatrixRowHeaderContainer
      ref={container}
      style={{
        width,
        gridTemplateRows: rowHeights.map(height => `${height}px`).join(' '),
        marginBottom: horizontalScrollbarHeight,
      }}
    >
      {rowModels.map((rowModel, index) => (
        <ComponentMatrixRowHeader
          key={index}
          rowModel={rowModel}
          rowIndex={index}
          rowBottomGaps={rowBottomGaps}
          rowHeights={rowHeights}
          hoveredComponentId={hoveredComponentId}
          focusedComponentId={focusedComponentId}
          contextComponentId={contextComponentId}
          commands={commands}
        />
      ))}
    </ComponentMatrixRowHeaderContainer>
  );
});

export { ComponentMatrixRowHeadersArea };
